"use client"

import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronLeft, ChevronRight, Star } from "lucide-react"

const testimonials = [
  {
    text: "I was terrified to start therapy, but from the first session I felt safe and understood. Battlefield Counseling helped me process years of trauma I'd been carrying alone.",
    author: "Client in North Carolina",
    rating: 5,
  },
  {
    text: "EMDR changed my life. I didn't think anything could help with my anxiety, but after just a few sessions, I started feeling like myself again.",
    author: "EMDR Client",
    rating: 5,
  },
  {
    text: "The team is compassionate, professional, and truly cares. I've recommended them to friends and family. This is what real therapy should feel like.",
    author: "Ongoing Client",
    rating: 5,
  },
]

const TestimonialsSection = () => {
  const [current, setCurrent] = useState(0)

  const next = () => setCurrent((c) => (c + 1) % testimonials.length)
  const prev = () => setCurrent((c) => (c - 1 + testimonials.length) % testimonials.length)

  return (
    <section className="section-padding !py-12 md:!py-16 bg-[hsl(var(--sage-dark))]">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-sm font-medium tracking-[0.18em] uppercase text-[hsl(var(--terracotta))] mb-3">
            Client Experiences
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-3 leading-snug">
            What Our Clients Are Saying
          </h2>
          <div className="w-14 h-0.5 bg-[hsl(var(--sage-light))] mx-auto mb-12" />
        </motion.div>

        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4 }}
              className="bg-white/10 rounded-2xl p-8 md:p-12 border border-white/20 shadow-sm"
            >
              <div className="flex justify-center gap-1 mb-6">
                {Array.from({ length: testimonials[current].rating }).map((_, i) => (
                  <Star
                    key={i}
                    className="w-5 h-5 fill-[hsl(var(--terracotta))] text-[hsl(var(--terracotta))]"
                  />
                ))}
              </div>
              <blockquote className="font-heading text-xl sm:text-2xl text-white italic leading-relaxed mb-6">
                &ldquo;{testimonials[current].text}&rdquo;
              </blockquote>
              <p className="text-sm text-white/60 font-medium uppercase tracking-wider">
                — {testimonials[current].author}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              onClick={prev}
              className="w-10 h-10 rounded-full border border-white/30 bg-white/10 flex items-center justify-center hover:border-white/60 hover:bg-white/20 transition-colors"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`h-2 rounded-full transition-all ${
                    i === current ? "bg-[hsl(var(--terracotta))] w-6" : "bg-white/30 w-2"
                  }`}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>
            <button
              onClick={next}
              className="w-10 h-10 rounded-full border border-white/30 bg-white/10 flex items-center justify-center hover:border-white/60 hover:bg-white/20 transition-colors"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default TestimonialsSection
